import { useState, useEffect } from 'react';
import { CustomLayout } from '@/components/ui/custom-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { templateMetadata } from '@/components/ResumeTemplates';
import { Template1, Template1ResumeData } from '@/components/ResumeTemplates/Template1';
import { Template11, Template11ResumeData } from '@/components/ResumeTemplates/Template11';
import { PaginatedTemplate1 } from '@/components/ResumeTemplates/PaginatedTemplate1';
import { PageMargins } from '@/utils/resumePagination';
import { FileText, Sparkles, Upload, ArrowLeft, Save, Download, Edit3, Eye, FileDown, Plus, ArrowUp, ArrowDown, Trash2, Maximize2, Wand2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { CustomTemplateService, CustomTemplate } from '@/services/customTemplateService';
import { useAuth } from '@/hooks/useAuth';

// Sample data for Template1 preview
const sampleTemplate1Data: Template1ResumeData = {
  Name: "Erica Merrick",
  Phone: "225-532-8794",
  Email: "emerrick2013@gmail.com",
  City: "Luling",
  State: "LA",
  LinkedIn: "www.linkedin.com/in/ericamerrick/",
  Summary: "Education Consultant with more than 20 years of experience in federal grant management and program compliance in K-12 education. Managed $40M+ in federal funds over tenure while securing $678K Freshman Academy grant and writing multiple successful grant applications totaling over $800K.",
  Skills: [
    { Skill: "Grant Writing" },
    { Skill: "Audit Preparation" },
    { Skill: "Policy Implementation" },
    { Skill: "Federal Grant Management" },
    { Skill: "Program Compliance" }
  ],
  CareerHighlights: [
    {
      chtitle: "Federal Grant Evaluation",
      chdescription: "Evaluated 16 federal grant proposals on 2 scoring panels spending 15 hours per week per review cycle while assessing 6 components with multiple criteria demonstrating federal evaluation capabilities."
    },
    {
      chtitle: "School Performance Growth",
      chdescription: "Raised school performance from D grade to A grade in 4 years while increasing dual enrollment 17% & AP pass rate by 0.7% demonstrating measurable academic improvement and leadership results."
    }
  ],
  ProfessionalExperience: [
    {
      job_title: "Peer Grant Reviewer",
      company: "U.S. Department of Education",
      location: "",
      start_date: "August 2024",
      end_date: "Present",
      Descriptions: [
        { Description: "Reviewed 16 federal grant proposals and thoroughly evaluated 6 components with 6 criteria per application cycle." },
        { Description: "Scored proposals based on overall quality and impact and ranked them within 2 collaborative review panels." }
      ]
    },
    {
      job_title: "Director of Federal Programs & School Improvement",
      company: "Algiers Charters",
      location: "New Orleans, LA",
      start_date: "July 2021",
      end_date: "October 2022",
      Descriptions: [
        { Description: "Researched and processed Title I–IV grants totaling $3M and secured $500K from 9th Grade Initiative Grant." },
        { Description: "Implemented $250K COVID tutoring and $56K REAL literacy programs serving underserved student populations." }
      ]
    }
  ],
  Education: [
    {
      College_Degree_Name: "Master of Education",
      Major: "Administration & Leadership",
      College_Name: "Nicholls State University",
      City: "",
      ST: "",
      Year_Earned: "2004"
    },
    {
      College_Degree_Name: "Bachelor of Arts",
      Major: "Criminal Justice",
      College_Name: "Dillard University",
      City: "",
      ST: "",
      Year_Earned: "1995"
    }
  ],
  CertificationsAndLicenses: [
    {
      Certification_or_License_Name: "Educational Leader Level 3",
      Institution_Organization: "Louisiana",
      Year: "2020"
    }
  ],
  AwardsAndHonors: [
    {
      Award_Name: "Regional Principal of the Year",
      Institution_Organization: "St. John the Baptist Parish",
      Year: ""
    }
  ]
};

export default function ResumeTemplates() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<string | null>(null);
  const [loadDataOpen, setLoadDataOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [jsonInput, setJsonInput] = useState('');
  const [customData, setCustomData] = useState<Template1ResumeData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editMode, setEditMode] = useState<'visual' | 'json'>('visual');
  const [margins, setMargins] = useState<PageMargins>({ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 });
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  // When an admin views the gallery (to spot-check what end users see), the
  // back arrow returns them to /admin/templates.  End users keep the normal
  // history-back behavior so the back arrow walks them back through their
  // tailoring flow.
  const galleryBackTo = isAdmin ? '/admin/templates' : undefined;
  const galleryTitle = isAdmin ? 'Resume Templates (admin preview)' : 'Resume Tailoring';

  // Database-backed custom templates (Wizard-published + uploaded). Fetched
  // on mount; merged into the displayed grid alongside the hardcoded ones.
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [customTemplatesLoading, setCustomTemplatesLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  useEffect(() => {
    let mounted = true;
    setCustomTemplatesLoading(true);
    CustomTemplateService.getUserTemplates()
      .then((rows) => { if (mounted) setCustomTemplates(rows); })
      .catch((err) => {
        console.warn('Failed to load custom templates:', err);
        // Non-fatal — gallery still shows the hardcoded set
      })
      .finally(() => { if (mounted) setCustomTemplatesLoading(false); });
    return () => { mounted = false; };
  }, []);

  // A custom-template row reshaped to match the displayed-tile metadata shape
  // so the existing render loop can iterate over both kinds uniformly.
  type DisplayTemplate = {
    id: string;                 // 'template-1' or a UUID for custom
    name: string;
    description: string;
    component?: string;         // present only for hardcoded
    category: string;
    features: string[];
    isCustom?: true;
    publicUrl?: string | null;
  };

  // Only show real Wizard-built / uploaded templates.  The legacy hardcoded
  // React designs (Template1…Template11) are kept in the codebase but are
  // NOT surfaced in the user-facing picker.
  const customAsDisplay: DisplayTemplate[] = customTemplates.map((c) => ({
    id: c.id,
    name: c.template_name,
    description: (c as any).style_metadata?.source === 'resume-template-wizard'
      ? 'Built in the Resume Template Wizard. Fills with your tailored resume data via Docmosis.'
      : 'Custom uploaded design. Fills with your tailored resume data via Docmosis.',
    category: (c as any).style_metadata?.source === 'resume-template-wizard' ? 'Wizard' : 'Custom Upload',
    features: ['Docmosis-rendered', 'Field-mapped', 'Pixel-perfect'],
    isCustom: true,
    publicUrl: c.template_file_url,
  }));
  const allTemplates: DisplayTemplate[] = customAsDisplay;

  const categories = ['all', ...new Set(allTemplates.map(t => t.category))];

  const filteredTemplates = selectedCategory === 'all'
    ? allTemplates
    : allTemplates.filter(t => t.category === selectedCategory);

  // Preview-docx modal state — uses Microsoft Office Online viewer so
  // the Supabase public URL renders inline without any backend round-trip.
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);
  const [docPreviewUrl, setDocPreviewUrl] = useState<string | null>(null);
  const [docPreviewName, setDocPreviewName] = useState<string>('');
  const openDocPreview = (url: string, name: string) => {
    setDocPreviewUrl(url);
    setDocPreviewName(name);
    setDocPreviewOpen(true);
  };

  // Find a custom template by ID
  const findCustom = (id: string) => customTemplates.find(c => c.id === id);

  const handleLoadJson = async () => {
    let parsed: any;
    try {
      parsed = JSON.parse(jsonInput);
    } catch (error) {
      toast({
        title: "Error",
        description: "Invalid JSON format. Please check your data.",
        variant: "destructive"
      });
      return;
    }

    // Branch by template kind: custom templates render via Docmosis,
    // hardcoded ones render via React component.
    const selectedCustom = selectedTemplate ? findCustom(selectedTemplate) : null;
    if (selectedCustom) {
      try {
        setGenerating(true);
        const blob = await CustomTemplateService.generateResumeFromTemplate(selectedCustom.id, parsed);
        CustomTemplateService.downloadBlob(blob, `${selectedCustom.template_name}.docx`);
        setLoadDataOpen(false);
        toast({
          title: "Resume generated",
          description: `Downloaded "${selectedCustom.template_name}.docx" using your data.`,
        });
      } catch (err: any) {
        toast({
          title: "Generation failed",
          description: err?.message || 'Could not generate the resume from this template.',
          variant: "destructive",
        });
      } finally {
        setGenerating(false);
      }
      return;
    }

    // Hardcoded React-rendered template
    setCustomData(parsed);
    setLoadDataOpen(false);
    setIsEditing(true);
    toast({
      title: "Success",
      description: "JSON data loaded successfully!"
    });
  };

  const handleUseTemplate = (templateId: string) => {
    setSelectedTemplate(templateId);
    setLoadDataOpen(true);
    setJsonInput('');
    setCustomData(null);
  };

  const handleBackToGallery = () => {
    setIsEditing(false);
    setCustomData(null);
    setSelectedTemplate(null);
  };

  const handleSave = () => {
    toast({
      title: "Saved",
      description: "Your resume has been saved!"
    });
  };

  const handleExportPDF = async () => {
    if (!customData) return;
    
    try {
      toast({
        title: "Generating PDF...",
        description: "This may take a moment..."
      });
      
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');
      
      // Get all resume pages
      const pages = document.querySelectorAll('.resume-page');
      if (pages.length === 0) {
        throw new Error('No resume pages found');
      }
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter'
      });
      
      // Process each page
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as HTMLElement;
        
        // Capture page as image
        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });
        
        const imgData = canvas.toDataURL('image/png');
        
        // Add page to PDF (if not first page)
        if (i > 0) {
          pdf.addPage();
        }
        
        // Add image to PDF page
        pdf.addImage(imgData, 'PNG', 0, 0, 8.5, 11);
      }
      
      // Download PDF
      const filename = `${customData.Name.replace(/\s+/g, '_')}_Resume.pdf`;
      pdf.save(filename);
      
      toast({
        title: "Success!",
        description: "Your resume has been exported to PDF."
      });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting your resume to PDF.",
        variant: "destructive"
      });
    }
  };

  const handleExportWord = async () => {
    if (!customData) return;
    
    try {
      toast({
        title: "Generating Word document...",
        description: "This may take a moment..."
      });
      
      const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } = await import('docx');
      
      // Create document sections
      const sections: any[] = [];
      
      // Header
      sections.push(
        new Paragraph({
          text: customData.Name.toUpperCase(),
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun(`${customData.Phone} • ${customData.City}, ${customData.State} • ${customData.Email}${customData.LinkedIn ? ` • ${customData.LinkedIn}` : ''}`)
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 }
        })
      );
      
      // Summary
      sections.push(
        new Paragraph({
          text: "Summary",
          heading: HeadingLevel.HEADING_2,
          alignment: AlignmentType.CENTER,
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          text: customData.Summary,
          alignment: AlignmentType.JUSTIFIED,
          spacing: { after: 200 }
        })
      );
      
      // Career Highlights
      if (customData.CareerHighlights && customData.CareerHighlights.length > 0) {
        sections.push(
          new Paragraph({
            text: "Career Highlights",
            heading: HeadingLevel.HEADING_2,
            alignment: AlignmentType.CENTER,
            spacing: { before: 200, after: 100 }
          })
        );
        customData.CareerHighlights.forEach(h => {
          sections.push(
            new Paragraph({
              children: [
                new TextRun({ text: '✓ ', bold: true }),
                new TextRun({ text: `${h.chtitle}: `, bold: true }),
                new TextRun({ text: h.chdescription })
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 100 }
            })
          );
        });
      }
      
      // Professional Experience
      if (customData.ProfessionalExperience && customData.ProfessionalExperience.length > 0) {
        sections.push(
          new Paragraph({
            text: "Experience",
            heading: HeadingLevel.HEADING_2,
            alignment: AlignmentType.CENTER,
            spacing: { before: 200, after: 100 }
          })
        );
        customData.ProfessionalExperience.forEach(job => {
          sections.push(
            new Paragraph({
              children: [
                new TextRun({ text: job.company, bold: true }),
                new TextRun({ text: job.location ? ` - ${job.location}` : '' })
              ],
              spacing: { after: 50 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: job.job_title, italics: true })
              ],
              spacing: { after: 50 }
            }),
            new Paragraph({
              text: `${job.start_date} - ${job.end_date}`,
              spacing: { after: 100 }
            })
          );
          job.Descriptions.forEach(desc => {
            sections.push(
              new Paragraph({
                text: desc.Description,
                bullet: { level: 0 },
                spacing: { after: 50 }
              })
            );
          });
        });
      }
      
      // Education
      sections.push(
        new Paragraph({
          text: "Education",
          heading: HeadingLevel.HEADING_2,
          alignment: AlignmentType.CENTER,
          spacing: { before: 200, after: 100 }
        })
      );
      customData.Education.forEach(edu => {
        sections.push(
          new Paragraph({
            children: [
              new TextRun({ text: edu.College_Name, bold: true })
            ],
            spacing: { after: 50 }
          }),
          new Paragraph({
            text: `${edu.College_Degree_Name}${edu.Major ? `, ${edu.Major}` : ''}${edu.Year_Earned ? ` – ${edu.Year_Earned}` : ''}`,
            spacing: { after: 100 }
          })
        );
      });
      
      // Skills
      sections.push(
        new Paragraph({
          text: "Skills",
          heading: HeadingLevel.HEADING_2,
          alignment: AlignmentType.CENTER,
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          text: customData.Skills.map(s => s.Skill).join(' • '),
          spacing: { after: 200 }
        })
      );
      
      // Create document
      const doc = new Document({
        sections: [{
          properties: {},
          children: sections
        }]
      });
      
      // Generate and download
      const blob = await Packer.toBlob(doc);
      const filename = `${customData.Name.replace(/\s+/g, '_')}_Resume.docx`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Success!",
        description: "Your resume has been exported to Word."
      });
    } catch (error) {
      console.error('Error exporting to Word:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting your resume to Word.",
        variant: "destructive"
      });
    }
  };

  const updateField = (path: string[], value: any) => {
    if (!customData) return;
    
    const newData = { ...customData };
    let current: any = newData;
    
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    
    current[path[path.length - 1]] = value;
    setCustomData(newData);
  };

  const updateMargin = (side: keyof PageMargins, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0.25 && numValue <= 1.5) {
      setMargins({ ...margins, [side]: numValue });
    }
  };

  const resetMargins = () => {
    setMargins({ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 });
  };

  const contentWidth = (8.5 - margins.left - margins.right).toFixed(2);
  const contentHeight = (11 - margins.top - margins.bottom).toFixed(2);

  // If editing, show editor view
  if (isEditing && customData) {
    return (
      <CustomLayout>
        <div className="min-h-screen bg-background">
          {/* Editor Header */}
          <div className="border-b bg-card sticky top-0 z-10">
            <div className="container mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button variant="ghost" size="icon" onClick={handleBackToGallery}>
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div>
                    <h1 className="text-xl font-bold">Resume Editor</h1>
                    <p className="text-sm text-muted-foreground">
                      {customData.Name}'s Resume
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Tabs value={editMode} onValueChange={(v) => setEditMode(v as 'visual' | 'json')}>
                    <TabsList>
                      <TabsTrigger value="visual">
                        <Edit3 className="w-4 h-4 mr-2" />
                        Visual
                      </TabsTrigger>
                      <TabsTrigger value="json">
                        <FileText className="w-4 h-4 mr-2" />
                        JSON
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Button variant="outline" onClick={handleSave}>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button variant="outline" onClick={handleExportPDF}>
                    <FileDown className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                  <Button onClick={handleExportWord}>
                    <Download className="w-4 h-4 mr-2" />
                    Export Word
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Editor Content */}
          <div className="container mx-auto px-4 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
              {/* Editing Panel */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Edit3 className="w-5 h-5" />
                      {editMode === 'visual' ? 'Edit Fields' : 'Edit JSON'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[calc(100vh-280px)]">
                      {editMode === 'visual' ? (
                        <div className="space-y-6 pr-4">
                          {/* Personal Info */}
                          <div className="space-y-4">
                            <h3 className="font-semibold text-sm">Personal Information</h3>
                            <div className="space-y-3">
                              <div>
                                <Label htmlFor="name">Name</Label>
                                <Input
                                  id="name"
                                  value={customData.Name}
                                  onChange={(e) => updateField(['Name'], e.target.value)}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label htmlFor="phone">Phone</Label>
                                  <Input
                                    id="phone"
                                    value={customData.Phone}
                                    onChange={(e) => updateField(['Phone'], e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="email">Email</Label>
                                  <Input
                                    id="email"
                                    value={customData.Email}
                                    onChange={(e) => updateField(['Email'], e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label htmlFor="city">City</Label>
                                  <Input
                                    id="city"
                                    value={customData.City}
                                    onChange={(e) => updateField(['City'], e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="state">State</Label>
                                  <Input
                                    id="state"
                                    value={customData.State}
                                    onChange={(e) => updateField(['State'], e.target.value)}
                                  />
                                </div>
                              </div>
                              <div>
                                <Label htmlFor="linkedin">LinkedIn</Label>
                                <Input
                                  id="linkedin"
                                  value={customData.LinkedIn || ''}
                                  onChange={(e) => updateField(['LinkedIn'], e.target.value)}
                                />
                              </div>
                            </div>
                          </div>

                          <Separator />

                          {/* Summary */}
                          <div className="space-y-3">
                            <h3 className="font-semibold text-sm">Professional Summary</h3>
                            <Textarea
                              value={customData.Summary}
                              onChange={(e) => updateField(['Summary'], e.target.value)}
                              className="min-h-[120px]"
                            />
                          </div>

                          <Separator />

                          {/* Experience */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-sm">Professional Experience</h3>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  const newExp = {
                                    company: '',
                                    job_title: '',
                                    location: '',
                                    start_date: '',
                                    end_date: '',
                                    Descriptions: []
                                  };
                                  updateField(['ProfessionalExperience'], [...customData.ProfessionalExperience, newExp]);
                                }}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Add Job
                              </Button>
                            </div>
                            
                            {customData.ProfessionalExperience.map((exp, idx) => (
                              <div key={idx} className="p-4 border rounded-lg space-y-3 bg-muted/30">
                                {/* Job header with delete/reorder */}
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-muted-foreground">Job {idx + 1}</span>
                                  <div className="flex gap-1">
                                    {idx > 0 && (
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        onClick={() => {
                                          const updated = [...customData.ProfessionalExperience];
                                          [updated[idx], updated[idx - 1]] = [updated[idx - 1], updated[idx]];
                                          updateField(['ProfessionalExperience'], updated);
                                        }}
                                      >
                                        <ArrowUp className="w-3 h-3" />
                                      </Button>
                                    )}
                                    {idx < customData.ProfessionalExperience.length - 1 && (
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        onClick={() => {
                                          const updated = [...customData.ProfessionalExperience];
                                          [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
                                          updateField(['ProfessionalExperience'], updated);
                                        }}
                                      >
                                        <ArrowDown className="w-3 h-3" />
                                      </Button>
                                    )}
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => {
                                        const updated = customData.ProfessionalExperience.filter((_, i) => i !== idx);
                                        updateField(['ProfessionalExperience'], updated);
                                      }}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Company */}
                                <div>
                                  <Label htmlFor={`exp-company-${idx}`}>Company</Label>
                                  <Input
                                    id={`exp-company-${idx}`}
                                    value={exp.company}
                                    onChange={(e) => updateField(['ProfessionalExperience', String(idx), 'company'], e.target.value)}
                                  />
                                </div>
                                
                                {/* Job Title */}
                                <div>
                                  <Label htmlFor={`exp-title-${idx}`}>Job Title</Label>
                                  <Input
                                    id={`exp-title-${idx}`}
                                    value={exp.job_title}
                                    onChange={(e) => updateField(['ProfessionalExperience', String(idx), 'job_title'], e.target.value)}
                                  />
                                </div>
                                
                                {/* Dates */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label htmlFor={`exp-start-${idx}`}>Start Date</Label>
                                    <Input
                                      id={`exp-start-${idx}`}
                                      placeholder="Jan 2020"
                                      value={exp.start_date}
                                      onChange={(e) => updateField(['ProfessionalExperience', String(idx), 'start_date'], e.target.value)}
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor={`exp-end-${idx}`}>End Date</Label>
                                    <Input
                                      id={`exp-end-${idx}`}
                                      placeholder="Present"
                                      value={exp.end_date}
                                      onChange={(e) => updateField(['ProfessionalExperience', String(idx), 'end_date'], e.target.value)}
                                    />
                                  </div>
                                </div>
                                
                                {/* Descriptions */}
                                <div>
                                  <Label htmlFor={`exp-descriptions-${idx}`}>Accomplishments (one per line)</Label>
                                  <Textarea
                                    id={`exp-descriptions-${idx}`}
                                    value={exp.Descriptions.map(d => d.Description).join('\n')}
                                    onChange={(e) => {
                                      const descriptions = e.target.value.split('\n').filter(d => d.trim()).map(d => ({ Description: d }));
                                      updateField(['ProfessionalExperience', String(idx), 'Descriptions'], descriptions);
                                    }}
                                    className="min-h-[100px] font-mono text-xs"
                                    placeholder="• Increased sales by 25%&#10;• Led team of 5 developers&#10;• Implemented new CRM system"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>

                          <Separator />

                          {/* Rules */}
                          <div className="space-y-3">
                            <h3 className="font-semibold text-sm">Resume Writing Rules</h3>
                            <Textarea
                              value={customData.Rules || ''}
                              onChange={(e) => updateField(['Rules'], e.target.value)}
                              className="min-h-[120px]"
                              placeholder="Enter resume writing rules and guidelines..."
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Textarea
                            value={JSON.stringify(customData, null, 2)}
                            onChange={(e) => {
                              try {
                                const parsed = JSON.parse(e.target.value);
                                setCustomData(parsed);
                              } catch (error) {
                                // Invalid JSON, don't update
                              }
                            }}
                            className="min-h-[calc(100vh-320px)] font-mono text-xs"
                          />
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Margin Controls */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Maximize2 className="w-5 h-5" />
                      Page Margins
                    </CardTitle>
                    <CardDescription>
                      Adjust page margins (inches)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="margin-top">Top</Label>
                        <Input
                          id="margin-top"
                          type="number"
                          step="0.1"
                          min="0.25"
                          max="1.5"
                          value={margins.top}
                          onChange={(e) => updateMargin('top', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="margin-bottom">Bottom</Label>
                        <Input
                          id="margin-bottom"
                          type="number"
                          step="0.1"
                          min="0.25"
                          max="1.5"
                          value={margins.bottom}
                          onChange={(e) => updateMargin('bottom', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="margin-left">Left</Label>
                        <Input
                          id="margin-left"
                          type="number"
                          step="0.1"
                          min="0.25"
                          max="1.5"
                          value={margins.left}
                          onChange={(e) => updateMargin('left', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="margin-right">Right</Label>
                        <Input
                          id="margin-right"
                          type="number"
                          step="0.1"
                          min="0.25"
                          max="1.5"
                          value={margins.right}
                          onChange={(e) => updateMargin('right', e.target.value)}
                        />
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Page Size:</span>
                        <span className="font-medium">8.5" × 11"</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Content Area:</span>
                        <span className="font-medium">{contentWidth}" × {contentHeight}"</span>
                      </div>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={resetMargins}
                    >
                      Reset to Default (0.5")
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Live Preview with Pagination */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Live Preview {selectedTemplate === 'template-1' ? '(Paginated)' : ''}
                    </CardTitle>
                    <CardDescription>
                      {selectedTemplate === 'template-1' 
                        ? 'Automatic page breaks for printing - Scroll to see all pages'
                        : 'Full resume preview - Scroll to see all content'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[calc(100vh-280px)] w-full">
                      <div className="bg-gray-100 p-8 min-h-full flex justify-center resume-pages-container">
                        {selectedTemplate === 'template-1' && (
                          <PaginatedTemplate1 data={customData} margins={margins} />
                        )}
                        {selectedTemplate === 'template-11' && (
                          <Template11 data={customData as any} />
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </CustomLayout>
    );
  }

  return (
    <CustomLayout title={galleryTitle} backTo={galleryBackTo}>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <div className="container mx-auto px-4 py-12 max-w-7xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <FileText className="w-12 h-12 text-primary" />
              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
                Resume Templates
              </h1>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose from our collection of professionally designed resume templates. Each template is ATS-friendly and optimized for your industry.
            </p>
          </div>

          {/* Category Filters */}
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-8">
            <TabsList className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-7 gap-2 h-auto p-1">
              {categories.map((category) => (
                <TabsTrigger 
                  key={category} 
                  value={category}
                  className="capitalize"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <Card
                key={template.id}
                className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/50 overflow-hidden"
              >
                {/* Template Preview Area */}
                <div className="relative bg-gradient-to-br from-muted/50 to-muted h-48 flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                  {template.isCustom ? (
                    <Wand2 className="w-20 h-20 text-primary/40 group-hover:text-primary/60 transition-colors duration-300" />
                  ) : (
                    <FileText className="w-20 h-20 text-primary/30 group-hover:text-primary/50 transition-colors duration-300" />
                  )}
                  <Badge
                    variant="secondary"
                    className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm"
                  >
                    {template.category}
                  </Badge>
                  {template.isCustom && (
                    <Badge
                      variant="default"
                      className="absolute top-3 left-3 gap-1 bg-primary/90 backdrop-blur-sm"
                    >
                      <Wand2 className="w-3 h-3" /> Custom
                    </Badge>
                  )}
                </div>

                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {template.name}
                    <Sparkles className="w-4 h-4 text-primary" />
                  </CardTitle>
                  <CardDescription>{template.description}</CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Features */}
                  <div className="flex flex-wrap gap-2">
                    {template.features.map((feature, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {feature}
                      </Badge>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {template.isCustom ? (
                      // Inline preview via Microsoft Office Online viewer
                      <Button
                        className="flex-1"
                        size="sm"
                        variant="outline"
                        disabled={!template.publicUrl}
                        onClick={() => template.publicUrl && openDocPreview(template.publicUrl, template.name)}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                      </Button>
                    ) : (
                      <Button
                        className="flex-1"
                        size="sm"
                        onClick={() => {
                          setPreviewTemplate(template.id);
                          setPreviewOpen(true);
                          setCustomData(null);
                        }}
                      >
                        Preview
                      </Button>
                    )}
                    <Button
                      variant={template.isCustom ? 'default' : 'outline'}
                      className="flex-1"
                      size="sm"
                      onClick={() => handleUseTemplate(template.id)}
                    >
                      Use Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredTemplates.length === 0 && !customTemplatesLoading && (
            <div className="text-center py-16">
              <Wand2 className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-muted-foreground mb-2">
                No templates available yet
              </h3>
              <p className="text-muted-foreground">
                Templates are published from the admin Template Wizard.
              </p>
            </div>
          )}
          {customTemplatesLoading && (
            <div className="text-center py-16 text-muted-foreground">
              Loading templates…
            </div>
          )}
        </div>

        {/* Inline docx preview — Office Online viewer renders the Supabase
            public URL.  Works for any *.docx that's publicly accessible. */}
        <Dialog open={docPreviewOpen} onOpenChange={setDocPreviewOpen}>
          <DialogContent className="max-w-5xl w-[92vw] h-[88vh] flex flex-col p-0">
            <DialogHeader className="px-6 py-4 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                {docPreviewName || 'Template preview'}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden bg-muted/30">
              {docPreviewUrl ? (
                <iframe
                  title={docPreviewName || 'Template preview'}
                  src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(docPreviewUrl)}`}
                  className="w-full h-full border-0"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No preview available
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t flex justify-between items-center gap-3">
              <p className="text-xs text-muted-foreground">
                Preview shows the source design with field tags. Click "Use This Template" to fill it with your data.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDocPreviewOpen(false)}>
                  Close
                </Button>
                {docPreviewUrl && (
                  <Button size="sm" asChild>
                    <a href={docPreviewUrl} target="_blank" rel="noreferrer" download>
                      <Download className="w-3.5 h-3.5 mr-1" />
                      Download raw .docx
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Load JSON Data Dialog */}
        <Dialog open={loadDataOpen} onOpenChange={setLoadDataOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Load Your Resume Data</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="json-input">Paste your JSON resume data below:</Label>
                <Textarea
                  id="json-input"
                  placeholder='{"Name": "Your Name", "Phone": "123-456-7890", ...}'
                  className="min-h-[300px] font-mono text-sm mt-2"
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setLoadDataOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleLoadJson} disabled={!jsonInput.trim() || generating}>
                  {generating ? (
                    <>
                      <span className="w-4 h-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
                      Generating .docx…
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Load Data
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {customData ? 'Your Resume' : 'Template Preview'}
              </DialogTitle>
            </DialogHeader>
            <div className="bg-gray-100 p-4 rounded-lg">
              {previewTemplate === 'template-1' && (
                <Template1 data={customData || sampleTemplate1Data} />
              )}
              {previewTemplate === 'template-11' && customData && (
                <Template11 data={customData as any} />
              )}
              {previewTemplate !== 'template-1' && previewTemplate !== 'template-11' && (
                <div className="text-center py-12 text-muted-foreground">
                  Preview coming soon for this template
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </CustomLayout>
  );
}
